import {
  activateCategory,
  getInactiveCategories,
} from "../../actions/categoryActions";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Spinner, Table } from "react-bootstrap";
import { usePermissions } from "../../utilities/usePermissions";
import { AppConfirmDialog } from "../../components/ui";

// ─── Power BI palette ─────────────────────────────────────────────────────────
const PBI = {
  bg: "#f3f2f1",
  surface: "#ffffff",
  headerBg: "#252423",
  headerText: "#ffffff",
  accent: "#e8563a",
  blue: "#118dff",
  blueSoft: "#e8f4ff",
  green: "#12b76a",
  greenSoft: "#e6f9f0",
  amber: "#f79009",
  amberSoft: "#fff8ec",
  grey: "#605e5c",
  greyLight: "#edebe9",
  border: "#d2d0ce",
  textPrimary: "#252423",
  textSecondary: "#605e5c",
  textMuted: "#a19f9d",
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, color, softColor, icon }) => (
  <div style={{
    background: PBI.surface, border: `1px solid ${PBI.border}`,
    borderTop: `3px solid ${color}`, borderRadius: "4px",
    padding: "18px 20px", minWidth: "160px", flex: "1",
  }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
      <span style={{ fontSize: "11px", fontWeight: "600", color: PBI.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </span>
      <div style={{ width: "28px", height: "28px", borderRadius: "4px", background: softColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <i className={icon} style={{ fontSize: "12px", color }} />
      </div>
    </div>
    <div style={{ fontSize: "28px", fontWeight: "700", color: PBI.textPrimary, lineHeight: 1 }}>
      {value}
    </div>
  </div>
);

// ─── Confirm Activate Modal ────────────────────────────────────────────────────
const ConfirmActivateModal = ({ item, onConfirm, onCancel }) => (
  <AppConfirmDialog
    open
    title="Activate this category?"
    consequence={<><strong>{item?.category}</strong> becomes visible to customers and available for listings.</>}
    confirmLabel="Activate"
    tone="primary"
    onConfirm={onConfirm}
    onClose={onCancel}
  />
);

// ─── Main Component ───────────────────────────────────────────────────────────
const DeactiveCategoriesTable = () => {
  const { inActiveCategories, loading } = useSelector(
    (state) => state.categories
  );

  const dispatch = useDispatch();
  const history = useHistory();
  const { can, loaded: permissionsLoaded } = usePermissions();
  const canManageCategories =
    permissionsLoaded && can("categorization:manage");
  const canCreateCategories =
    canManageCategories ||
    (permissionsLoaded && can("categorization:create"));
  const canUpdateCategories =
    canManageCategories ||
    (permissionsLoaded && can("categorization:update"));
  const [search, setSearch] = useState("");
  const [confirmActivate, setConfirmActivate] = useState(null);

  useEffect(() => {
    dispatch(getInactiveCategories());
  }, []);

  const filteredCategories = (inActiveCategories || []).filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (c.category || "").toLowerCase().includes(q) ||
      (c._id || "").toLowerCase().includes(q)
    );
  });

  const totalListings = (inActiveCategories || []).reduce(
    (sum, c) => sum + (c.post_listings?.length || 0), 0
  );

  const thStyle = {
    background: PBI.headerBg, color: PBI.headerText,
    fontSize: "10px", fontWeight: "700", textTransform: "uppercase",
    letterSpacing: "0.5px", padding: "11px 16px", whiteSpace: "nowrap",
    borderBottom: "2px solid #3b3a39", borderRight: "1px solid #3b3a39",
  };
  const tdStyle = {
    verticalAlign: "middle", padding: "11px 16px",
    fontSize: "13px", color: PBI.textPrimary,
    borderBottom: `1px solid ${PBI.greyLight}`,
  };

  return (
    <div style={{ padding: "24px 28px", background: PBI.bg, minHeight: "100vh" }}>

      {/* ── Page header ────────────────────────────────────────────────────── */}
      <div style={{
        background: PBI.headerBg, borderRadius: "4px",
        padding: "20px 28px", marginBottom: "20px",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{ width: "40px", height: "40px", background: PBI.accent, borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="fas fa-toggle-off" style={{ color: "#fff", fontSize: "16px" }} />
          </div>
          <div>
            <div style={{ color: PBI.headerText, fontSize: "18px", fontWeight: "700", lineHeight: 1.2 }}>
              Deactivated Categories
            </div>
            <div style={{ color: PBI.textMuted, fontSize: "12px", marginTop: "3px" }}>
              All inactive categories — reactivate to make visible in listings
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={() => dispatch(getInactiveCategories())}
            style={{
              background: "transparent", border: `1px solid #605e5c`,
              color: PBI.textMuted, borderRadius: "3px", padding: "6px 14px",
              fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px",
            }}
          >
            <i className="fas fa-sync-alt" style={{ fontSize: "11px" }} />
            Refresh
          </button>
          <button
            onClick={() => history.push("/admin/category-create")}
            disabled={!canCreateCategories}
            style={{
              background: PBI.accent, border: "none", color: "#fff",
              borderRadius: "3px", padding: "6px 16px",
              fontSize: "12px", fontWeight: "700", cursor: "pointer",
              display: "flex", alignItems: "center", gap: "6px",
            }}
          >
            <i className="nc-icon nc-simple-add" style={{ fontSize: "11px" }} />
            Add Category
          </button>
        </div>
      </div>

      {/* ── KPI cards ──────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: "14px", marginBottom: "20px", flexWrap: "wrap" }}>
        <KpiCard label="Inactive Categories" value={inActiveCategories?.length || 0} color={PBI.accent}  softColor="#fdf0ed"       icon="fas fa-toggle-off" />
        <KpiCard label="Total Listings"       value={totalListings}                    color={PBI.amber}  softColor={PBI.amberSoft} icon="fas fa-list" />
        <KpiCard label="Awaiting Activation"  value={(inActiveCategories || []).filter(c => c.active_status === "Inactive").length} color={PBI.green} softColor={PBI.greenSoft} icon="fas fa-toggle-on" />
      </div>

      {/* ── Main card ──────────────────────────────────────────────────────── */}
      <div style={{ background: PBI.surface, border: `1px solid ${PBI.border}`, borderRadius: "4px", overflow: "hidden" }}>

        {/* Toolbar */}
        <div style={{
          padding: "14px 20px", borderBottom: `1px solid ${PBI.greyLight}`,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px",
        }}>
          <span style={{ fontSize: "13px", fontWeight: "600", color: PBI.textPrimary }}>
            All Inactive Categories
          </span>
          <div style={{ position: "relative", minWidth: "240px" }}>
            <i className="fas fa-search" style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: PBI.textMuted, fontSize: "12px", pointerEvents: "none" }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search category name or ID..."
              style={{
                paddingLeft: "30px", paddingRight: search ? "28px" : "10px",
                paddingTop: "7px", paddingBottom: "7px",
                border: `1px solid ${PBI.border}`, borderRadius: "3px",
                fontSize: "12px", width: "100%", outline: "none",
                color: PBI.textPrimary, background: PBI.bg,
              }}
            />
            {search && (
              <span
                onClick={() => setSearch("")}
                style={{ position: "absolute", right: "9px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: PBI.textMuted, fontSize: "13px" }}
              >
                ✕
              </span>
            )}
          </div>
        </div>

        {/* Result count */}
        {search && (
          <div style={{ padding: "7px 20px", background: PBI.blueSoft, borderBottom: `1px solid #b3d9ff`, fontSize: "12px", color: PBI.blue }}>
            <i className="fas fa-filter" style={{ marginRight: "6px" }} />
            Showing <strong>{filteredCategories.length}</strong> of <strong>{inActiveCategories?.length || 0}</strong> categories
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px" }}>
            <Spinner animation="border" style={{ color: PBI.accent, width: "36px", height: "36px" }} />
            <div style={{ marginTop: "12px", color: PBI.textSecondary, fontSize: "13px" }}>Loading categories…</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table style={{ marginBottom: 0, width: "100%" }}>
              <thead>
                <tr>
                  {["#", "Category", "Image", "Listings", "Category ID", "Action"].map((h, i) => (
                    <th key={h} style={{ ...thStyle, borderRight: i === 5 ? "none" : undefined }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCategories.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "52px 20px", color: PBI.textSecondary }}>
                      <i className="fas fa-check-circle" style={{ fontSize: "24px", color: PBI.green, display: "block", marginBottom: "10px" }} />
                      <span style={{ fontSize: "14px", fontWeight: "600" }}>
                        {search ? "No categories match your search" : "No inactive categories — all categories are active!"}
                      </span>
                    </td>
                  </tr>
                ) : (
                  filteredCategories.map((item, index) => (
                    <tr
                      key={item?._id}
                      style={{ background: index % 2 === 0 ? PBI.surface : "#faf9f8" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = PBI.greyLight; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = index % 2 === 0 ? PBI.surface : "#faf9f8"; }}
                    >
                      {/* # */}
                      <td style={{ ...tdStyle, color: PBI.textMuted, fontSize: "12px", width: "40px", textAlign: "center" }}>
                        {index + 1}
                      </td>

                      {/* Category name */}
                      <td style={{ ...tdStyle, fontWeight: "600", minWidth: "150px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <div style={{ width: "28px", height: "28px", borderRadius: "4px", background: "#fdf0ed", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <i className="fas fa-tag" style={{ fontSize: "11px", color: PBI.accent }} />
                          </div>
                          {item?.category || <em style={{ color: PBI.textMuted, fontStyle: "normal" }}>—</em>}
                        </div>
                      </td>

                      {/* Image */}
                      <td style={{ ...tdStyle, textAlign: "center", minWidth: "100px" }}>
                        <img
                          src={item?.category_pic || item?.displayPic || "https://res.cloudinary.com/lusuku/image/upload/v1702635550/Lusuku_Public_Resources/No_Category_Pic_Placecholder-Prod_ltsbhf.jpg"}
                          alt={item?.category || "Category"}
                          style={{ width: "64px", height: "40px", objectFit: "cover", borderRadius: "4px", border: `1px solid ${PBI.border}` }}
                        />
                      </td>

                      {/* Listings count */}
                      <td style={{ ...tdStyle, textAlign: "center", minWidth: "90px" }}>
                        <span style={{
                          background: PBI.amberSoft, color: PBI.amber,
                          border: `1px solid #fddfa2`, borderRadius: "12px",
                          padding: "3px 10px", fontSize: "12px", fontWeight: "700",
                        }}>
                          {item?.post_listings?.length || 0}
                        </span>
                      </td>

                      {/* Category ID */}
                      <td style={{ ...tdStyle, minWidth: "200px" }}>
                        <span style={{
                          fontFamily: "monospace", fontSize: "11px",
                          color: PBI.textSecondary, background: PBI.greyLight,
                          padding: "3px 8px", borderRadius: "3px",
                          border: `1px solid ${PBI.border}`,
                        }}>
                          {item?._id}
                        </span>
                      </td>

                      {/* Action */}
                      <td style={{ ...tdStyle, minWidth: "120px" }}>
                        {item?.active_status === "Inactive" ? (
                          <button
                            title="Activate Category"
                            disabled={!canUpdateCategories}
                            onClick={(e) => {
                              e.preventDefault();
                              if (canUpdateCategories) setConfirmActivate(item);
                            }}
                            style={{
                              padding: "6px 14px", borderRadius: "3px", border: "none",
                              background: PBI.green, color: "#fff",
                              fontSize: "11px", fontWeight: "700", cursor: "pointer",
                              display: "inline-flex", alignItems: "center", gap: "5px",
                              whiteSpace: "nowrap",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                          >
                            <i className="fas fa-toggle-on" style={{ fontSize: "11px" }} />
                            Activate
                          </button>
                        ) : (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", color: PBI.green, fontSize: "12px", fontWeight: "600" }}>
                            <i className="fas fa-check-circle" />
                            Active
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}

        {/* Footer */}
        {!loading && filteredCategories.length > 0 && (
          <div style={{ padding: "10px 20px", borderTop: `1px solid ${PBI.greyLight}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "12px", color: PBI.textMuted }}>
              {filteredCategories.length} categor{filteredCategories.length !== 1 ? "ies" : "y"}
            </span>
            <button
              onClick={() => history.push("/admin/category-create")}
              disabled={!canCreateCategories}
              style={{
                background: "transparent", border: `1px solid ${PBI.border}`,
                color: PBI.textSecondary, borderRadius: "3px", padding: "5px 14px",
                fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px",
              }}
            >
              <i className="nc-icon nc-simple-add" style={{ fontSize: "11px" }} />
              Add Category
            </button>
          </div>
        )}
      </div>

      {/* ── Confirm Activate Modal ──────────────────────────────────────────── */}
      {confirmActivate && (
        <ConfirmActivateModal
          item={confirmActivate}
          onConfirm={() => {
            if (canUpdateCategories) {
              dispatch(activateCategory(confirmActivate._id));
            }
            setConfirmActivate(null);
          }}
          onCancel={() => setConfirmActivate(null)}
        />
      )}
    </div>
  );
};

export default DeactiveCategoriesTable;
